import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  brandAvatarGlowClass,
  brandGradFillClass,
  cn,
} from '@up2stay/up2promo-ui';

import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import type { AutosaveCueStatus } from '@/core/hooks/use-autosave-cue';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';

type SettingsIdentityStripProps = {
  photoPath?: string;
  displayName: string;
  username?: string;
  email?: string;
  initials: string;
  autosaveStatus?: AutosaveCueStatus;
  photoControls?: React.ReactNode;
  className?: string;
  /** `card` = own surface; `plain` = row content inside a SettingsGroup. */
  surface?: 'card' | 'plain';
};

export function SettingsIdentityStrip({
  photoPath,
  displayName,
  username,
  email,
  initials,
  autosaveStatus,
  photoControls,
  className,
  surface = 'card',
}: SettingsIdentityStripProps) {
  const avatarSrc = photoPath ? getAbsoluteFileUrl(photoPath) : undefined;

  return (
    <div
      className={cn(
        surface === 'card' &&
          'rounded-[var(--r-settings-group,12px)] bg-card p-5 shadow-[var(--shadow-card)] sm:p-6',
        surface === 'plain' && 'p-0',
        className,
      )}
    >
      <div className="flex items-start gap-4">
        <Avatar
          className={cn('size-16 shrink-0 md:size-20', brandAvatarGlowClass)}
        >
          {avatarSrc ? <AvatarImage src={avatarSrc} alt={displayName} /> : null}
          <AvatarFallback
            className={cn('text-base font-semibold', brandGradFillClass)}
          >
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[15.5px] font-bold leading-tight text-foreground [overflow-wrap:anywhere]">
              {displayName}
            </p>
            {autosaveStatus ? (
              <AutosaveStatusCue status={autosaveStatus} className="shrink-0" />
            ) : null}
          </div>
          {username ? (
            <p className="text-[13.5px] leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
              @{username}
            </p>
          ) : null}
          {email ? (
            <p className="truncate text-[13.5px] leading-relaxed text-muted-foreground">
              {email}
            </p>
          ) : null}
          {photoControls ? (
            <div className="flex flex-wrap gap-2 pt-1">{photoControls}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
